import { useMutation, useQuery } from "@tanstack/react-query";
import { Commodity } from "../../models/Commodity";
import { CommoditySubstitute } from "../../models/CommoditySubstitute";
import {
  deleteCommodity,
  getAvailableCommoditiesForSwitch,
  getCommodities,
  getCommoditySubstitutes,
  upsertCommodity,
  upsertCommoditySubstitutes,
} from "../../repositories/CommoditiesRepository";

// =============================================================================
// Commodity Mutations
// =============================================================================
// Cache invalidation is handled by SignalR (see useRealtimeInvalidation).
// Only UpsertCommoditySubstitutes (last call in chain) is mapped as a source
// to avoid duplicate SignalR messages from the two sequential API calls.

interface UpsertCommodityWithSubstitutesParams {
  commodity: Commodity;
  substituteCommodityIds: number[];
}

export const useUpsertCommodityWithSubstitutesMutation = () => {
  return useMutation({
    mutationFn: async ({
      commodity,
      substituteCommodityIds,
    }: UpsertCommodityWithSubstitutesParams) => {
      // Step 1: Upsert commodity
      const commodityResp = await upsertCommodity(commodity);
      if (!commodityResp.ok) {
        throw new Error(commodityResp.message);
      }
      const entityId = commodityResp.data.tables[0].rows[0].Id as number;

      // Step 2: Upsert substitutes (this triggers SignalR invalidation)
      const substitutesResp = await upsertCommoditySubstitutes(
        entityId,
        substituteCommodityIds,
      );
      if (!substitutesResp.ok) {
        throw new Error(substitutesResp.message);
      }

      return { entityId, commodityResp, substitutesResp };
    },
  });
};

export const useDeleteCommodityMutation = () => {
  return useMutation({
    mutationFn: async (commodityId: number) => {
      const resp = await deleteCommodity(commodityId);
      if (!resp.ok) {
        throw new Error(resp.message);
      }
      return resp;
    },
  });
};

export const useCommoditiesQuery = (
  onlyEnabled?: boolean,
  onError?: (error: Error) => void,
) => {
  return useQuery({
    queryKey: ["commodities", onlyEnabled],
    queryFn: async ({ signal }) => {
      const resp = await getCommodities(onlyEnabled, signal);
      if (resp.ok) {
        return (resp.data?.tables?.[0]?.rows ?? []) as Commodity[];
      }
      throw new Error(resp.message);
    },
    onError,
  });
};

export const useCommoditySubstitutesQuery = (
  commodityId: number | null,
  onlyEnabled?: boolean,
  onError?: (error: Error) => void,
) => {
  return useQuery({
    queryKey: ["commodity-substitutes", commodityId, onlyEnabled],
    queryFn: async ({ signal }) => {
      if (!commodityId) return [];
      const resp = await getCommoditySubstitutes(
        commodityId,
        onlyEnabled,
        signal,
      );
      if (resp.ok) {
        return (resp.data?.tables?.[0]?.rows ?? []) as CommoditySubstitute[];
      }
      throw new Error(resp.message);
    },
    enabled: !!commodityId,
    onError,
  });
};

export interface AvailableCommoditiesForSwitchResult {
  commodities: Commodity[];
  hasSubstitutes: boolean;
}

export const useAvailableCommoditiesForSwitchQuery = (
  commodityId: number | null,
) => {
  return useQuery({
    queryKey: ["commodity-switch", commodityId],
    queryFn: async ({
      signal,
    }): Promise<AvailableCommoditiesForSwitchResult> => {
      const resp = await getAvailableCommoditiesForSwitch(commodityId, signal);
      if (resp.ok) {
        const rows = resp.data?.tables?.[0]?.rows ?? [];
        const commodities = rows as Commodity[];
        const hasSubstitutes = (rows[0]?.HasSubstitutes ?? false) as boolean;
        return { commodities, hasSubstitutes };
      }
      throw new Error(resp.message);
    },
    enabled: !!commodityId,
  });
};
